import { z } from 'zod';
import { ConflictSchema, IsoInstantSchema, MoneyCzkSchema, PageQuerySchema, UuidSchema, } from './common.js';
import { ReservationStateSchema } from './enums.js';
import { AirshipResponseSchema } from './airship.dto.js';
import { HubResponseSchema } from './hub.dto.js';
import { UserResponseSchema } from './auth.dto.js';
export const PriceBreakdownSchema = z.object({
    billableHours: z.number().int().positive(),
    hourlyRateCzk: MoneyCzkSchema,
    flightCostCzk: MoneyCzkSchema,
    handlingFeeCzk: MoneyCzkSchema,
    passengerFeeCzk: MoneyCzkSchema,
    totalCzk: MoneyCzkSchema,
});
export const CreateReservationRequestSchema = z
    .object({
    airshipId: z.number().int().positive(),
    departureHubId: z.number().int().positive(),
    arrivalHubId: z.number().int().positive(),
    startAt: IsoInstantSchema,
    endAt: IsoInstantSchema,
    passengers: z.number().int().min(1).max(200),
    pilotId: z.number().int().positive().optional(),
    purpose: z.string().max(1000).optional(),
})
    .refine((value) => new Date(value.endAt).getTime() > new Date(value.startAt).getTime(), {
    message: 'endAt must be strictly after startAt',
    path: ['endAt'],
});
/**
 * BR-09: the approval trail. Null while no approval has ever been requested,
 * which is every reservation of an airship that does not require one.
 */
export const ApprovalTrailSchema = z.object({
    requestedAt: IsoInstantSchema,
    /** min(requestedAt + APPROVAL_WINDOW_HOURS, startAt), with the retroactive exception. */
    deadlineAt: IsoInstantSchema,
    decidedAt: IsoInstantSchema.nullable(),
    decidedBy: UserResponseSchema.nullable(),
    /** Reason on rejection; optional note on approval. */
    note: z.string().nullable(),
});
export const ReservationResponseSchema = z.object({
    id: UuidSchema,
    state: ReservationStateSchema,
    airship: AirshipResponseSchema,
    renter: UserResponseSchema,
    pilot: UserResponseSchema.nullable(),
    departureHub: HubResponseSchema,
    arrivalHub: HubResponseSchema,
    startAt: IsoInstantSchema,
    endAt: IsoInstantSchema,
    passengers: z.number().int().positive(),
    priceCzk: MoneyCzkSchema,
    priceBreakdown: PriceBreakdownSchema,
    purpose: z.string().nullable(),
    cancelledReason: z.string().nullable(),
    /** BR-09; null when this reservation never needed an approval. */
    approval: ApprovalTrailSchema.nullable(),
    createdAt: IsoInstantSchema,
    updatedAt: IsoInstantSchema,
    version: z.number().int().nonnegative(),
    /** Transitions the current caller may perform right now. */
    allowedTransitions: z.array(ReservationStateSchema),
    /** Refund the renter would get if they cancelled now. */
    refundIfCancelledNowCzk: MoneyCzkSchema,
});
/** Compact row for lists, dispatch board and approval queue. */
export const ReservationSummarySchema = z.object({
    id: UuidSchema,
    state: ReservationStateSchema,
    airshipId: z.number().int().positive(),
    airshipRegistration: z.string(),
    airshipName: z.string(),
    renterId: z.number().int().positive(),
    renterName: z.string(),
    departureHubCode: z.string(),
    arrivalHubCode: z.string(),
    startAt: IsoInstantSchema,
    endAt: IsoInstantSchema,
    passengers: z.number().int().positive(),
    priceCzk: MoneyCzkSchema,
    turnaroundMinutes: z.number().int().nonnegative(),
    /** BR-09: set while the reservation is PENDING_APPROVAL, so the queue can show the clock. */
    approvalDeadlineAt: IsoInstantSchema.nullable(),
    requiresApproval: z.boolean(),
});
export const ReservationListQuerySchema = PageQuerySchema.extend({
    state: ReservationStateSchema.optional(),
    airshipId: z.coerce.number().int().positive().optional(),
    mine: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => value === true || value === 'true')
        .optional(),
    from: IsoInstantSchema.optional(),
    to: IsoInstantSchema.optional(),
});
export const CancelReservationRequestSchema = z.object({
    reason: z.string().min(3).max(500),
});
/** OP-05: approving may carry a note; rejecting requires a reason. */
export const ApproveReservationRequestSchema = z.object({
    note: z.string().max(500).optional(),
});
export const RejectReservationRequestSchema = z.object({
    reason: z.string().min(3).max(500),
});
/** Returned by POST /reservations/:id/confirm when the rules reject the confirmation. */
export const ConfirmRejectionSchema = z.object({
    conflicts: z.array(ConflictSchema),
});
export const QuoteRequestSchema = z.object({
    airshipId: z.number().int().positive(),
    startAt: IsoInstantSchema,
    endAt: IsoInstantSchema,
    passengers: z.number().int().min(1).max(200),
});
//# sourceMappingURL=reservation.dto.js.map